import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { IEvent, EventStatus } from '../../../../../interfaces/event';
import {
  useNotification,
  NOTIFICATION_TYPES,
} from '../../../../../components/Notification';
import ParticipantsService from '../../../../../services/ParticipantsService';
import ROUTES from '../../../../../constants/routes';

import {
  getTimeFormat,
  convertToDate,
  getDifferenceInMinutes,
} from '../../../../../helpers/datetime';
import { MINUTES_ALLOW_TO_JOIN } from '../../../../../ek/modules/dashboard/components/HomeBottomBar/OfficeHoursModal/constants';
import { IRoom } from '../../../../../interfaces/rooms';

const useJoinEventNotification = ({
  currentEvent,
  currentRoom,
  isStreamLoaded,
}: {
  currentEvent?: IEvent;
  currentRoom: IRoom | null;
  isStreamLoaded: boolean;
}) => {
  const skipSlotIds = useRef<string[]>([]);
  const eventsTimeoutId = useRef<NodeJS.Timeout | null>(null);

  const timeParticipantEnteredEventRef = useRef<number | null>(null);

  const [slots, setSlots] = useState<IEvent[]>([]);

  const { showJoinEventNotification } = useNotification();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleJoinEvent = useCallback((eventId?: string) => {
    if (eventId) {
      navigate(ROUTES.EVENT_PAGE(eventId));
      window.location.reload();
    }
  }, []);

  const showJoinNotification = useCallback(
    ({ name, startTime, eventId }: IEvent) => {
      const startDate = getTimeFormat(convertToDate(startTime));

      const message = (
        <>
          Your <b>{name}</b> will start at {startDate}
        </>
      );

      showJoinEventNotification({
        message,
        type: NOTIFICATION_TYPES.SUCCESS,
        closeManually: true,
        onJoin: () => {
          handleJoinEvent(eventId);
        },
      });
    },
    [showJoinEventNotification, handleJoinEvent],
  );

  const showJoinStartedNotification = useCallback(
    ({ name, eventId }: IEvent) => {
      const message = (
        <>
          Your <b>{name}</b> has already started
        </>
      );

      showJoinEventNotification({
        message,
        type: NOTIFICATION_TYPES.SUCCESS,
        closeManually: true,
        onJoin: () => {
          handleJoinEvent(eventId);
        },
      });
    },
    [showJoinEventNotification, handleJoinEvent],
  );

  const validateSlotsToJoin = useCallback(
    async (slots: IEvent[], startedSlotsToJoin: IEvent[]) => {
      try {
        const participantEvents =
          await ParticipantsService.getCurrentParticipantEvents();

        if (!participantEvents.events.length) {
          return;
        }

        const eventIds = participantEvents.events
          .filter(
            (eventParticipant) =>
              eventParticipant.event?.status === EventStatus.ACTIVE,
          )
          .map((eventParticipant) => eventParticipant.event?.eventId);

        const newProcessedSlots: string[] = [];
        slots.forEach((slot) => {
          if (eventIds.includes(slot.eventId)) {
            showJoinNotification(slot);
            newProcessedSlots.push(slot.eventId);
          }
        });

        startedSlotsToJoin.forEach((slot) => {
          if (eventIds.includes(slot.eventId)) {
            showJoinStartedNotification(slot);
          }
        });

        skipSlotIds.current = [...skipSlotIds.current, ...newProcessedSlots];
      } catch (error) {
        //
      }
    },
    [showJoinNotification, showJoinStartedNotification],
  );

  const fetchParticipantEvents = useCallback(async (eventId: string) => {
    try {
      const participantEvents =
        await ParticipantsService.getCurrentParticipantEvents();

      if (!participantEvents.events.length) {
        setSlots([]);
        return;
      }

      const events = participantEvents.events
        .filter(
          (eventParticipant) =>
            eventParticipant.event &&
            eventParticipant.event.eventId !== eventId &&
            !(eventParticipant.event?.eventTags ?? []).includes(
              'FullTimeEvent',
            ) &&
            [EventStatus.SCHEDULED, EventStatus.ACTIVE].includes(
              eventParticipant.event.status,
            ),
        )
        .map((eventParticipant) => eventParticipant.event as IEvent);

      setSlots(events);
    } catch (error) {
      //
    }

    eventsTimeoutId.current = setTimeout(() => {
      fetchParticipantEvents(eventId);
    }, 5 * 60 * 1000);
  }, []);

  useEffect(() => {
    if (!currentEvent?.eventId || !timeParticipantEnteredEventRef.current) {
      return;
    }

    fetchParticipantEvents(currentEvent.eventId);

    return () => {
      if (eventsTimeoutId.current) {
        clearTimeout(eventsTimeoutId.current);
        eventsTimeoutId.current = null;
      }
    };
  }, [currentEvent?.eventId, timeParticipantEnteredEventRef.current]);

  useEffect(() => {
    if (
      timeParticipantEnteredEventRef.current ||
      !currentRoom ||
      !isStreamLoaded ||
      !currentEvent?.eventId ||
      pathname !== ROUTES.EVENT_PAGE(currentEvent.eventId)
    ) {
      return;
    }

    timeParticipantEnteredEventRef.current = Date.now();
  }, [currentRoom, isStreamLoaded, pathname, currentEvent?.eventId]);

  useEffect(() => {
    timeParticipantEnteredEventRef.current = null;

    return () => {
      if (eventsTimeoutId.current) {
        clearTimeout(eventsTimeoutId.current);
        eventsTimeoutId.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!slots || !slots.length) {
      return;
    }

    let timeoutId: NodeJS.Timeout | null = null;

    const checkSlotsToJoin = async () => {
      const slotsToJoin: IEvent[] = [];
      const startedSlotsToJoin: IEvent[] = [];

      const participantJoinedAt = timeParticipantEnteredEventRef.current
        ? new Date(timeParticipantEnteredEventRef.current)
        : 0;

      slots.forEach((slot) => {
        const minutesBeforeEventStar = getDifferenceInMinutes(slot.startTime);

        if (
          minutesBeforeEventStar < MINUTES_ALLOW_TO_JOIN &&
          minutesBeforeEventStar >= 0 &&
          !skipSlotIds.current.includes(slot.eventId)
        ) {
          //Events are about to start
          slotsToJoin.push(slot);
        }

        if (
          participantJoinedAt &&
          minutesBeforeEventStar < 0 &&
          participantJoinedAt < convertToDate(slot.startTime)
        ) {
          //Slots already started
          startedSlotsToJoin.push(slot);
        }
      });

      if (slotsToJoin.length || startedSlotsToJoin.length) {
        await validateSlotsToJoin(slotsToJoin, startedSlotsToJoin);
      }

      timeoutId = setTimeout(checkSlotsToJoin, 60 * 1000);
    };

    checkSlotsToJoin();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [slots, validateSlotsToJoin]);
};

export default useJoinEventNotification;
